// Pure helpers shared by SettingsEditor and RenderOptionsDialog. All frame-vs-
// seconds conversion happens here so both the tuning panel (frames -> seconds
// for display) and the render dialog (seconds preset -> frames for backend)
// use the same math and can never drift from ProjectView's duration estimate.

export const clamp = (value, min, max) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return min;
  if (num < min) return min;
  if (num > max) return max;
  return num;
};

export const secondsToFrames = (seconds, fps) => {
  if (!Number.isFinite(seconds) || !Number.isFinite(fps) || fps <= 0) return 0;
  const frames = Math.round(seconds * fps);
  return frames < 0 ? 0 : frames;
};

export const framesToSeconds = (frames, fps) => {
  if (!Number.isFinite(frames) || !Number.isFinite(fps) || fps <= 0) return 0;
  return frames / fps;
};

export const formatSeconds = (seconds) => {
  if (!Number.isFinite(seconds)) return '0';
  const rounded = Math.round(seconds * 100) / 100;
  // Trim trailing zeros (e.g. "1.00" -> "1", "0.50" -> "0.5").
  const s = rounded.toFixed(2);
  return s.replace(/\.?0+$/, '') || '0';
};

// Factory defaults MUST mirror the `default_settings` dict in
// handle_analyze_video_request —
// packages/containers/analyzevideo/processing/motion_analysis.py:1626-1633
// (the analyzer writes these into Firestore at analysis time). Keep them in
// sync — the "Reset all" button relies on this producing the same values
// the backend would.
//
// v2 (motionAlgorithmVersion === 2) replaces the legacy flat
// `min_motion_segment_length: 90` with a length-proportional default:
//   min(90, max(1, round(0.20 * nPairs)))     where nPairs = motionValues.length
// Mirrors the Python default the v2 analyzer writes at analysis time. The
// extra args are optional + backward-compatible: unversioned callers still
// get the legacy 90 so nothing shifts under a v1 project.
export const getDefaultSettings = (
  fps,
  calculatedMotionThreshold,
  { motionAlgorithmVersion, motionValueCount } = {}
) => {
  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : 30;
  const isV2 = motionAlgorithmVersion === 2;
  const nPairs = Number.isFinite(motionValueCount) && motionValueCount > 0
    ? motionValueCount
    : 0;
  const minMotionSegmentLength = isV2 && nPairs > 0
    ? Math.min(90, Math.max(1, Math.round(0.20 * nPairs)))
    : 90;
  return {
    motionThreshold: Number.isFinite(calculatedMotionThreshold) ? calculatedMotionThreshold : 0,
    max_window_size: Math.round(safeFps / 2),
    min_motion_frames_in_window: 1,
    min_motion_segment_length: minMotionSegmentLength,
    min_no_motion_segment_length: Math.round(safeFps * 0.5),
  };
};

export const getTransitionPresets = (fps) => {
  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : 30;
  return [
    {
      id: 'cut',
      label: 'Cut',
      description: 'Instant jump between clips',
      seconds: 0,
      frames: 0,
    },
    {
      id: 'quick',
      label: 'Quick fade',
      description: 'Snappy half-second crossfade',
      seconds: 0.5,
      frames: Math.round(safeFps * 0.5),
    },
    {
      id: 'smooth',
      label: 'Smooth fade',
      description: 'Relaxed one-second blend',
      seconds: 1,
      frames: Math.round(safeFps),
    },
  ];
};

export const matchTransitionPreset = (frames, fps) => {
  const preset = getTransitionPresets(fps).find((p) => p.frames === frames);
  return preset ? preset.id : 'custom';
};

// Duration math for the final rendered video. Mirrors the memo previously in
// ProjectView.js (kept identical so the render dialog's live estimate and the
// stats panel's "Edited Duration" never diverge — and stays in sync with
// backend billing in packages/functions/billing/background/main.py).
//
// `crossfadeCount` is how many of the clip boundaries actually fade. It
// defaults to "all of them", which is what every render did before capture-gap
// segmentation. Pass the real count (`renderCrossfadeCount`, lib/editDraft.js)
// on a project with capture gaps: a day boundary that hard-cuts would
// otherwise be charged a transition's worth of overlap it never pays, and the
// readout runs short.
//
// BOTH motion modes come through here. A MotionPace render is measured in the
// paced video's own output frames rather than source frames, but it is still a
// clip list paying a crossfade at each join — `pacedRenderPlan` (lib/editDraft.js)
// builds that list, and the arithmetic below is identical from there on.
export const computeEditedDurationSeconds = (
  motionSegments,
  outputFps,
  transitionFrames,
  crossfadeCount = null,
) => {
  if (!Array.isArray(motionSegments) || motionSegments.length === 0) return 0;
  if (!Number.isFinite(outputFps) || outputFps <= 0) return 0;

  const totalFrames = motionSegments.reduce(
    (acc, seg) => acc + Math.max(
      0,
      seg && Number.isFinite(seg.end) && Number.isFinite(seg.start) ? seg.end - seg.start : 0
    ),
    0
  );
  const safeTransition = Math.max(0, Number.isFinite(transitionFrames) ? transitionFrames : 0);
  const maxFades = Math.max(0, motionSegments.length - 1);
  const fades = Number.isFinite(crossfadeCount)
    ? Math.max(0, Math.min(crossfadeCount, maxFades))
    : maxFades;
  const transitionOverlap = fades * safeTransition;
  const effectiveFrames = Math.max(0, totalFrames - transitionOverlap);
  const seconds = effectiveFrames / outputFps;
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
};
